import { useEffect, useRef, useState } from 'react';

/**
 *
 * A hook for debouncing value changes. To immediately update value with first
 * call set the { leading: true } option.
 *
 * The hook returns a tuple with the debounced value and a cancel function.
 *
 */
const useDebouncedValue = (
  value: unknown,
  wait: number,
  options?: { leading?: boolean },
) => {
  const { leading } = { leading: false, ...options };
  const [debouncedValue, setDebouncedValue] = useState(value);
  const mountedRef = useRef(false);
  const timeoutRef = useRef<number>(undefined);
  const cooldownRef = useRef(false);

  const cancel = () => window.clearTimeout(timeoutRef.current);

  useEffect(() => {
    if (mountedRef.current) {
      if (!cooldownRef.current && leading) {
        cooldownRef.current = true;
        setDebouncedValue(value);
      } else {
        cancel();
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          setDebouncedValue(value);
        }, wait);
      }
    }
  }, [value, leading, wait]);

  useEffect(() => {
    mountedRef.current = true;
    return cancel;
  }, []);

  return [debouncedValue, cancel] as const;
};

export default useDebouncedValue;
